import React, { useState } from "react";
import DiffView from "./DiffView";
import SnapshotView from "./SnapshotView";
import { formatDateTimeInClientTimezone } from "Utils/utils";

const ACTION_CONFIG = {
  created: {
    icon: "➕",
    color: "text-green-600",
    bg: "bg-green-50",
    label: "Created",
  },
  updated: {
    icon: "✏️",
    color: "text-blue-600",
    bg: "bg-blue-50",
    label: "Updated",
  },
  deleted: {
    icon: "🔴",
    color: "text-red-600",
    bg: "bg-red-50",
    label: "Deleted",
  },
  activated: {
    icon: "✅",
    color: "text-green-600",
    bg: "bg-green-50",
    label: "Activated",
  },
  deactivated: {
    icon: "⚠️",
    color: "text-orange-600",
    bg: "bg-orange-50",
    label: "Deactivated",
  },
  status_changed: {
    icon: "🔄",
    color: "text-purple-600",
    bg: "bg-purple-50",
    label: "Status Changed",
  },
  approved: {
    icon: "✅",
    color: "text-green-600",
    bg: "bg-green-50",
    label: "Approved",
  },
  rejected: {
    icon: "❌",
    color: "text-red-600",
    bg: "bg-red-50",
    label: "Rejected",
  },
  submitted: {
    icon: "📤",
    color: "text-blue-600",
    bg: "bg-blue-50",
    label: "Submitted",
  },
  login: {
    icon: "🔑",
    color: "text-gray-600",
    bg: "bg-gray-50",
    label: "Login",
  },
  logout: {
    icon: "🚪",
    color: "text-gray-600",
    bg: "bg-gray-50",
    label: "Logout",
  },
  bulk_deleted: {
    icon: "🔴",
    color: "text-red-600",
    bg: "bg-red-50",
    label: "Bulk Deleted",
  },
  copied: {
    icon: "📋",
    color: "text-blue-600",
    bg: "bg-blue-50",
    label: "Copied",
  },
  published: {
    icon: "📢",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    label: "Published",
  },
  enabled: {
    icon: "✅",
    color: "text-green-600",
    bg: "bg-green-50",
    label: "Enabled",
  },
  disabled: {
    icon: "⚠️",
    color: "text-orange-600",
    bg: "bg-orange-50",
    label: "Disabled",
  },
  sent: {
    icon: "✉️",
    color: "text-blue-600",
    bg: "bg-blue-50",
    label: "Sent",
  },
  password_set: {
    icon: "🔑",
    color: "text-green-600",
    bg: "bg-green-50",
    label: "Password Set",
  },
  association_removed: {
    icon: "➖",
    color: "text-orange-600",
    bg: "bg-orange-50",
    label: "Removed",
  },
  viewed: {
    icon: "👁️",
    color: "text-gray-600",
    bg: "bg-gray-50",
    label: "Viewed",
  },
  shift_created: {
    icon: "📅",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    label: "Shift Created",
  },
};

const DEFAULT_CONFIG = {
  icon: "📝",
  color: "text-gray-600",
  bg: "bg-gray-50",
  label: "Action",
};

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const ActivityTimelineItem = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const config = ACTION_CONFIG[activity.action] || DEFAULT_CONFIG;
  const metadata = activity.metadata || {};
  const hasDetails = metadata.changes || metadata.snapshot;

  return (
    <div
      className={`border-l-2 pl-4 pb-4 ${config.color.replace("text-", "border-")}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 flex-1">
          <span
            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm ${config.bg}`}
          >
            {config.icon}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}
              >
                {config.label}
              </span>
              <span className="text-sm text-gray-700">
                by <strong>{activity.user?.name || "System"}</strong>
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {activity.description}
            </p>
            {hasDetails && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-blue-500 hover:text-blue-700 mt-1 focus:outline-none"
              >
                {expanded ? "Hide details ▲" : "View details ▼"}
              </button>
            )}
            {expanded && metadata.changes && (
              <DiffView changes={metadata.changes} />
            )}
            {expanded && metadata.snapshot && (
              <SnapshotView
                snapshot={metadata.snapshot}
                relatedCounts={metadata.related_counts}
              />
            )}
            {expanded && metadata.old_status !== undefined && (
              <div className="mt-2 text-xs">
                <span className="text-red-500 line-through">
                  {String(metadata.old_status)}
                </span>
                <span className="mx-2">→</span>
                <span className="text-green-600 font-medium">
                  {String(metadata.new_status)}
                </span>
              </div>
            )}
          </div>
        </div>
        <div
          className="text-xs text-gray-400 whitespace-nowrap ml-3"
          title={formatDateTimeInClientTimezone(activity.created_at)}
        >
          {formatRelativeTime(activity.created_at)}
        </div>
      </div>
    </div>
  );
};

const ActivityTimeline = ({
  activities,
  loading,
  onLoadMore,
  hasMore,
  compact = false,
  emptyMessage = "No activity found.",
}) => {
  if (loading && (!activities || activities.length === 0)) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-0" : "space-y-1"}>
      {activities.map((activity) => (
        <ActivityTimelineItem key={activity.id} activity={activity} />
      ))}
      {hasMore && onLoadMore && (
        <div className="text-center pt-2">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="text-sm text-blue-500 hover:text-blue-700 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Load more..."}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;
